import React, { useState } from "react";
import { Form, Button, Container, Card } from "react-bootstrap";
import axiosInstance from "../api/axiosInstance";
import { CiDeliveryTruck } from "react-icons/ci";
import moment from "moment";
import { ClipLoader } from "react-spinners";
import { 
    FaTruckMoving, 
    FaSearch, 
    FaUser, 
    FaBuilding, 
    FaMapMarkerAlt, 
    FaCalendarAlt, 
    FaInfoCircle 
} from "react-icons/fa";

const DeliveryTracker = () => {
    const couriers = [
        { code: "kr.cjlogistics", name: "CJ대한통운" },
        { code: "kr.lotte", name: "롯데택배" },
        { code: "kr.hanjin", name: "한진택배" },
        { code: "kr.logen", name: "로젠택배" },
        { code: "kr.epost", name: "우체국택배" },
        { code: "kr.kdexp", name: "경동택배" },
        { code: "kr.hdexp", name: "합동택배" },
        { code: "de.dhl", name: "DHL" },
        { code: "us.fedex", name: "Fedex" },
        { code: "us.ups", name: "UPS" },
        { code: "us.upss", name: "UPSS" },
        { code: "kr.ilyanglogis", name: "일양로지스" },
        { code: "kr.chunilps", name: "천일택배" },
        { code: "kr.cupost", name: "CU택배" },
        { code: "kr.cvsnet", name: "GS택배" },
        { code: "kr.cway", name: "CWAY" },
        { code: "kr.daesin", name: "대신택배" },
        { code: "kr.hanips", name: "한의사랑택배" },
        { code: "kr.homepick", name: "홈픽" },
        { code: "kr.honamlogis", name: "한서호남택배" },
        { code: "kr.kunyoung", name: "건영택배" },
        { code: "kr.slx", name: "SLX" },
        { code: "nl.tnt", name: "TNT" },
        { code: "un.upu.ems", name: "EMS" },
    ];

    const [selectedCourier, setSelectedCourier] = useState("kr.cjlogistics");
    const [trackingNumber, setTrackingNumber] = useState("");
    const [trackingHistory, setTrackingHistory] = useState([]);
    const [shipmentInfo, setShipmentInfo] = useState(null);
    const [loading, setLoading] = useState(false);

    // 최근 검색한 운송장 번호 리스트 (localStorage 사용)
    const [recentTrackings, setRecentTrackings] = useState(
        JSON.parse(localStorage.getItem("recentTrackings")) || []
    );

    // API 호출 (운송장 조회)
    const fetchTrackingInfo = async (cCode = selectedCourier, tNum = trackingNumber) => {
        const targetNum = tNum.trim();
        if (!targetNum) {
            alert("운송장 번호를 입력하세요.");
            return;
        }

        setLoading(true);
        try {
            const response = await axiosInstance.get(`https://apis.tracker.delivery/carriers/${cCode}/tracks/${targetNum}`);

            // 배송 정보 업데이트
            setShipmentInfo({
                carrier: response.data.carrier,
                from: response.data.from,
                to: response.data.to,
                state: response.data.state
            });
            setTrackingHistory(response.data.progresses || []);

            // 최근 검색 기록 처리 (중복 제거 및 최대 3개로 제한)
            const storedTrackings = JSON.parse(localStorage.getItem("recentTrackings")) || [];

            // 중복 제거 후 새로운 운송장 번호와 택배사 코드 추가
            const updatedTrackings = [
                { trackingNumber: targetNum, courier: cCode },
                ...storedTrackings.filter(item => item.trackingNumber !== targetNum)
            ].slice(0, 3);

            // 상태 업데이트 및 localStorage에 저장
            setRecentTrackings(updatedTrackings);
            localStorage.setItem("recentTrackings", JSON.stringify(updatedTrackings));

        } catch (error) {
            alert("운송장 조회에 실패했습니다. 번호나 택배사를 다시 확인해 주세요.");
        }
        setLoading(false);
    };

    // 최근 조회 목록에서 삭제
    const removeTrackingNumber = (tracking) => {
        const updatedTrackings = recentTrackings.filter(item => item.trackingNumber !== tracking.trackingNumber);
        setRecentTrackings(updatedTrackings);
        localStorage.setItem("recentTrackings", JSON.stringify(updatedTrackings));
    };

    // 최근 조회 목록에서 선택 시 셀렉트박스와 인풋박스에 자동 입력 후 바로 조회
    const selectTrackingFromList = (tracking) => {
        setTrackingNumber(tracking.trackingNumber);
        setSelectedCourier(tracking.courier);
        fetchTrackingInfo(tracking.courier, tracking.trackingNumber);
    };

    // 현재 배송 상태에 따른 스텝 지수 매핑
    const getStateId = () => {
        if (!shipmentInfo || !shipmentInfo.state) return '';
        if (typeof shipmentInfo.state === 'object') {
            return shipmentInfo.state.id || '';
        }
        return String(shipmentInfo.state);
    };

    const getStepIndex = (stateVal) => {
        if (!stateVal) return 0;
        const lower = stateVal.toLowerCase();
        if (lower.includes('delivered') || lower.includes('완료')) return 4;
        if (lower.includes('out_for_delivery') || lower.includes('출발') || lower.includes('배달중')) return 3;
        if (lower.includes('in_transit') || lower.includes('배송중') || lower.includes('이동중') || lower.includes('허브') || lower.includes('터미널')) return 2;
        if (lower.includes('at_carrier') || lower.includes('접수') || lower.includes('인수') || lower.includes('수거')) return 1;
        return 0; // 준비중
    };

    const stateId = getStateId();
    const currentStep = getStepIndex(stateId);
    const progressPercent = currentStep * 25; // 0, 25, 50, 75, 100

    // 최신 시간순(내림차순) 정렬을 위한 이력 뒤집기
    const sortedHistory = [...trackingHistory].reverse();

    return (
        <div className="delivery-wrapper">
            {
                loading && (
                    <div className="loading-overlay">
                        <div className="loading-spinner">
                            <ClipLoader color="#ffffff" size={50} />
                            <p className="mt-2">운송장 조회 중...</p>
                        </div>
                    </div>
                )
            }
            <Container>
                {/* 검색 필터 카드 */}
                <div className="delivery-search-card">
                    <h4 className="fw-bold d-flex align-items-center gap-2 mb-3">
                        <CiDeliveryTruck className="text-primary" /> 택배 배송 실시간 추적
                    </h4>
                    
                    <div className="d-flex gap-2">
                        <Form.Select 
                            value={selectedCourier} 
                            onChange={(e) => setSelectedCourier(e.target.value)} 
                            className="medical-select"
                            style={{ width: '160px' }}
                        >
                            {couriers.map(courier => (
                                <option key={courier.code} value={courier.code}>{courier.name}</option>
                            ))}
                        </Form.Select>
                        <Form.Control
                            type="text"
                            placeholder="운송장 번호를 기입해 주세요."
                            value={trackingNumber}
                            onChange={(e) => setTrackingNumber(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    fetchTrackingInfo();
                                }
                            }}
                            className="medical-search-input"
                        />
                        <Button className="medical-search-btn" onClick={() => fetchTrackingInfo()}>
                            <FaSearch className="me-1" /> 조회
                        </Button>
                    </div>

                    {/* 최근 검색 목록 */}
                    {recentTrackings.length > 0 && (
                        <div className="mt-3">
                            <div className="text-muted small fw-bold mb-1">최근 검색 운송장</div>
                            <div className="recent-tracking-list">
                                {recentTrackings.map((tracking, index) => {
                                    const courierName = couriers.find(c => c.code === tracking.courier)?.name || '미확인';
                                    return (
                                        <div 
                                            key={index} 
                                            className="recent-tracking-chip"
                                            onClick={() => selectTrackingFromList(tracking)}
                                        >
                                            <span>{courierName} : {tracking.trackingNumber}</span>
                                            <button 
                                                className="delete-btn"
                                                onClick={(e) => { 
                                                    e.stopPropagation(); 
                                                    removeTrackingNumber(tracking); 
                                                }}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {/* 배송 추적 애니메이션 게이지 */}
                {shipmentInfo && (
                    <div className="delivery-gauge-card">
                        <h5 className="fw-bold mb-4 d-flex align-items-center gap-2">
                            <FaTruckMoving className="text-primary" /> 배송 진척 현황
                        </h5>

                        {/* 수평 배송 트랙 */}
                        <div className="delivery-track-bg">
                            <div className="delivery-track-fill" style={{ width: `${progressPercent}%` }}></div>
                            
                            {/* 트럭 주행 애니메이션 */}
                            <div className="delivery-truck-icon-wrapper" style={{ left: `${progressPercent}%` }}>
                                <FaTruckMoving size={20} />
                            </div>

                            {/* 각 배송 노드 */}
                            <div className="delivery-nodes">
                                <div className={`delivery-node ${currentStep >= 1 ? 'active' : ''}`}>
                                    <span className="delivery-node-label">상품접수</span>
                                </div>
                                <div className={`delivery-node ${currentStep >= 2 ? 'active' : ''}`}>
                                    <span className="delivery-node-label">배송중</span>
                                </div>
                                <div className={`delivery-node ${currentStep >= 3 ? 'active' : ''}`}>
                                    <span className="delivery-node-label">배송출발</span>
                                </div>
                                <div className={`delivery-node ${currentStep >= 4 ? 'active' : ''}`}>
                                    <span className="delivery-node-label">배송완료</span>
                                </div>
                            </div>
                        </div>

                        {/* 화물 기본정보 개편 카드 */}
                        <div className="p-3 bg-light rounded-3 d-flex flex-wrap gap-4 mt-5" style={{ fontSize: '0.9rem' }}>
                            <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ minWidth: '200px' }}>
                                <FaBuilding className="text-primary" size={20} />
                                <div>
                                    <div className="text-muted small">택배사 / 고객센터</div>
                                    <div className="fw-bold text-dark">{shipmentInfo.carrier.name} ({shipmentInfo.carrier.tel || '전화 없음'})</div>
                                </div>
                            </div>
                            <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ minWidth: '150px' }}>
                                <FaUser className="text-success" size={20} />
                                <div>
                                    <div className="text-muted small">보내는 사람 (송화인)</div>
                                    <div className="fw-bold text-dark">{shipmentInfo.from.name || '-'}</div>
                                </div>
                            </div>
                            <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ minWidth: '150px' }}>
                                <FaUser className="text-info" size={20} />
                                <div>
                                    <div className="text-muted small">받는 사람 (수화인)</div>
                                    <div className="fw-bold text-dark">{shipmentInfo.to.name || '-'}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* 배송 진행 이력 세로 타임라인 */}
                {trackingHistory.length > 0 && (
                    <Card className="delivery-timeline-card">
                        <Card.Body className="p-0">
                            <h5 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                <FaInfoCircle className="text-primary" /> 실시간 배송 히스토리
                            </h5>
                            
                            <div className="delivery-timeline">
                                {sortedHistory.map((item, index) => (
                                    <div className="timeline-item" key={index}>
                                        <div className="timeline-badge"></div>
                                        <div className="timeline-content">
                                            <div className="timeline-header">
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className="timeline-location">
                                                        <FaMapMarkerAlt size={12} className="text-danger me-1" />
                                                        {item.location.name || '처리지점'}
                                                    </span>
                                                    <span className="timeline-status">
                                                        {item.status.text}
                                                    </span>
                                                </div>
                                                <span className="timeline-time">
                                                    <FaCalendarAlt size={12} className="me-1" />
                                                    {moment(item.time).format('YYYY-MM-DD HH:mm:ss')}
                                                </span>
                                            </div>
                                            <div className="timeline-desc">
                                                {item.description}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card.Body>
                    </Card>
                )}
            </Container>
        </div>
    );
};

export { DeliveryTracker };
